import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserDetails, AvailabilityObject } from '../../types/schemas/abc';
import { getFirstName, getInitial } from '../../utils/name';
import {
  TutorInfo,
  TagWrap,
  TutorName,
  Tag,
  TutorDetailWrapper2,
  BannerIconButton,
  PrimaryButton,
  SecondaryButton,
  ScheduleGrid,
  GridHeaderCell,
  TimeHeaderCell,
  ScheduleCell,
  ScheduleKeyContainer,
  ScheduleKeyItem,
  ScheduleKeyColorBox,
  ScheduleKeyLabel,
  ProfileInitial,
  DetailFooter,
  DetailContent,
  DetailCardHeader,
  DetailPageShell3,
  DetailContentArea,
  ButtonText,
} from '../../styles/search/search.style';
import {
  DescriptionText,
  GridHeader,
  GridSubjectLabel,
  GridTickCell,
  KeyIconWrapper,
  SubheaderContainer,
  SubheaderLine,
  SubheaderTitle,
  SubjectsGrid,
} from '../../styles/profile/form.style';
import { CheckLg, XCircleFill, Search, ChatLeftText, X } from 'react-bootstrap-icons';
import { useConnections } from '../../hooks/useConnections';
import { daysOfWeekLogic, unrollAvailability } from '../../utils/detailView';
import { getDetectedTimezone, shortTzName, friendlyTzName } from '../../utils/timezones';
import { Globe } from 'lucide-react';
import { useSubjectGrid } from '../../hooks/useSubjectGrid';
import { useAvailability } from '../../hooks/useAvailability';
import { GRANULARITY, adjustAvailabilityPerDay, DAYS_MONDAY_FIRST, getTimezoneOffset } from '../../utils/availability';

// NEED TO ADD IN SAME GRANULARITY ON TIME OVERLAP TO TUTORDETAILVIEW


interface ParentDetailViewProps {
  tutor: UserDetails;
  parent: UserDetails;
  onClose: () => void;
  showActions?: boolean;
}

export const ParentDetailView: React.FC<ParentDetailViewProps> = ({
  tutor,
  parent,
  onClose,
  showActions = true,
}) => {
  const { createTutorConnection } = useConnections();
  const navigate = useNavigate(); // Initialize useNavigate
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const tutorExpertiseMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (tutor?.expertise) {
      for (const [subject, levels] of Object.entries(tutor.expertise)) {
        map.set(subject, new Set(levels));
      }
    }
    return map; 

  }, [tutor?.expertise]) 

  const parentExpertiseMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (parent?.expertise) {
      for (const [subject, levels] of Object.entries(parent.expertise)) {
        map.set(subject, new Set(levels))
      }
    }
    return map;

  }, [parent?.expertise]);

  const { 
    subjects: parentSubjects, 
    sortedLevels: parentLevels, 
    gridColumns 
  } = useSubjectGrid(parent?.expertise)


  const browserTz = useMemo(() => getDetectedTimezone(), []);
  const homeTz = tutor?.timezone || 'Europe/London';
  const parentTz = parent?.timezone;

  const tzEntries = useMemo(() => {
    const tzMap = new Map<string, { key: string; parts: string[] }>();
    const homeEntry = tzMap.get(homeTz) || { key: 'home', parts: [] };
    homeEntry.parts.push('Home');
    tzMap.set(homeTz, homeEntry);
    const browserEntry = tzMap.get(browserTz) || { key: 'browser', parts: [] };
    browserEntry.parts.push('Current');
    tzMap.set(browserTz, browserEntry);
    if (parentTz) {
      const pEntry = tzMap.get(parentTz) || { key: 'parent', parts: [] };
      pEntry.parts.push("Parent's");
      tzMap.set(parentTz, pEntry);
    }
    return Array.from(tzMap.entries()).map(([tz, { key, parts }]) => ({
      key, tz, label: `${parts.join(' / ')} timezone (${friendlyTzName(tz)})`,
    }));
  }, [homeTz, browserTz, parentTz]);

  const [activeTzKey, setActiveTzKey] = useState('home');
  const activeTz = tzEntries.find(e => e.key === activeTzKey)?.tz || homeTz;
  const tzSwitchButtons = tzEntries.filter(e => e.key !== activeTzKey);

  const buildDayOffsets = (fromTz: string, toTz: string): number[] => {
    const now = new Date();
    const todayMon = (now.getDay() + 6) % 7;
    return DAYS_MONDAY_FIRST.map((_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() + (i - todayMon));
      return getTimezoneOffset(toTz, fromTz, d);
    });
  };

  const tutorAvailInActiveTz = useMemo(() => {
    if (!tutor?.availability || activeTz === homeTz) return tutor?.availability;
    return adjustAvailabilityPerDay(tutor.availability, buildDayOffsets(homeTz, activeTz!), DAYS_MONDAY_FIRST);
  }, [tutor?.availability, activeTz, homeTz]);

  const parentAvailInActiveTz = useMemo(() => {
    if (!parent?.availability || !parentTz || activeTz === parentTz) return parent?.availability;
    return adjustAvailabilityPerDay(parent.availability, buildDayOffsets(parentTz, activeTz!), DAYS_MONDAY_FIRST);
  }, [parent?.availability, parentTz, activeTz]);

  const {availabilityStatusMap, hoursOfDay} = useAvailability(
      { availability: tutorAvailInActiveTz, timezone: activeTz },
      { availability: parentAvailInActiveTz, timezone: activeTz }
      );

  const timezoneNote = useMemo(() => {
    if (!activeTz || !parent?.timezone) return null;
    if (activeTz === parent.timezone) return `Availability overlap shown in ${friendlyTzName(activeTz)}.`;
    return `Parent's timezone: ${friendlyTzName(parent.timezone)}. Shown in your time (${friendlyTzName(activeTz)}).`;
  }, [activeTz, parent?.timezone]);

  const tutorSubjectsSet = useMemo(() => {
    if (!tutor?.subjects) return new Set<string>();

    return new Set(tutor.subjects);
  }, [tutor?.subjects]);

  // This targets your specific container div instead of the whole window
  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, []); // The empty dependency array ensures this runs only once when the component mounts


  const daysOfWeek = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];



  const handleSaveAndSearch = async () => {
    try {
      // The status 'saved' is for when the user just wants to save and keep looking
      await createTutorConnection({ parentId: parent.id, status: 'saved' });
      onClose(); // Close the modal on success
    } catch (error) {
      console.error('Failed to save connection:', error);
    }
  };

  const handleSaveAndMessage = async () => {
    try {
      // The status 'contacted' indicates the parent wants to start a conversation
      const newConnection = await createTutorConnection({
        parentId: parent.id,
        status: 'contacted',
      });
      if (newConnection) {
        navigate(`/messenger?open=${newConnection.data.id}`);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save and message:', error);
    }
  };

  const initials = getInitial(parent.name);
  const tutorLevelsBySubject = useMemo(
    () => tutor.expertise || {},
    [tutor.expertise]
  );

  return (
    <DetailPageShell3>
      {/* <DetailContentArea> */}
        <DetailCardHeader>
          {getFirstName(parent?.name)}
          <BannerIconButton
            onClick={() => {
              onClose(); // This is already functional and closes the view
            }}
          >
            <XCircleFill size={18} />
          </BannerIconButton>
        </DetailCardHeader>

        <DetailContent ref={scrollContainerRef}>
          <TutorDetailWrapper2>
            <ProfileInitial
              view={'detail'}
              style={{ backgroundColor: parent.profileColor || '#e0e0e0' }}
            >
              {initials}
            </ProfileInitial>
            <TutorInfo isDetailedView={true}>
              <TutorName>{getFirstName(parent.name) || 'Parent'}</TutorName>

              {parent.subjects && parent.subjects.length > 0 && (
                <TagWrap detailedView={true}>
                  {parent.subjects.map((subject) => {
                    const isMatch = tutorSubjectsSet.has(subject);
                    return (
                      <Tag key={subject} isMatch={isMatch}>
                        {subject}
                      </Tag>
                    );
                  })}
                </TagWrap>
              )}
            </TutorInfo>
          </TutorDetailWrapper2>
          <SubheaderContainer>
            <SubheaderLine />
            <SubheaderTitle>Description</SubheaderTitle>
            <SubheaderLine />
          </SubheaderContainer>

          <DescriptionText
            // Conditionally add a class if the description doesn't exist
            className={!parent?.description ? 'not-provided' : ''}
          >
            {parent?.description || 'No description provided yet.'}
          </DescriptionText>

          <SubheaderContainer>
            <SubheaderLine />
            <SubheaderTitle>Subjects Required</SubheaderTitle>
            <SubheaderLine />
          </SubheaderContainer>

          {parentSubjects.length > 0 && (
            <ScheduleKeyContainer>
              <ScheduleKeyItem>
                <KeyIconWrapper color="match">
                  <CheckLg size={14} />
                </KeyIconWrapper>
                <ScheduleKeyLabel>You teach this</ScheduleKeyLabel>
              </ScheduleKeyItem>
              <ScheduleKeyItem>
                <KeyIconWrapper color="partial">
                  <CheckLg size={14} />
                </KeyIconWrapper>
                <ScheduleKeyLabel>You teach this subject, different level</ScheduleKeyLabel>
              </ScheduleKeyItem>
              <ScheduleKeyItem>
                <KeyIconWrapper color="notTaught">
                  <X size={14} />
                </KeyIconWrapper>
                <ScheduleKeyLabel>Not covered</ScheduleKeyLabel>
              </ScheduleKeyItem>
            </ScheduleKeyContainer>
          )}

          {parentSubjects.length > 0 ? (
            <>
              <SubjectsGrid
                css={{
                  display: 'grid',
                  gridTemplateColumns: gridColumns,
                  overflowX: 'auto',
                }}
              >
                <GridHeader />
                {parentLevels.map(({ level }) => (
                  <GridHeader key={level}>{level}</GridHeader>
                ))}


                {parentSubjects.map((subject) => {
                  const requiredLevels = parentExpertiseMap.get(subject) || new Set();

                  return (
                    <React.Fragment key={subject}>
                      <GridSubjectLabel>{subject}</GridSubjectLabel>
                      {parentLevels.map(({ level }) => {
                        const tutorHasIt = tutorExpertiseMap.get(subject)?.has(level);
                        const parentNeedsIt = requiredLevels.has(level);

                        if (tutorHasIt && parentNeedsIt) {
                          return (
                            <GridTickCell key={level} status="exact">
                              <CheckLg size={18} />
                            </GridTickCell>
                          );
                        }

                        // Case 2: Tutor has it, but Parent didn't ask for it -> Partial/Available
                        if (tutorHasIt) {
                          return (
                            <GridTickCell key={level} status="partial">
                               <CheckLg size={18} />
                            </GridTickCell>
                          );
                        }

                        if (parentNeedsIt && !tutorHasIt) {
                          return (
                            <GridTickCell key={level} status="notTaught">
                               <X size={18} />
                            </GridTickCell>
                          );
                        }


                        // Case 3: Tutor does not have it -> Empty
                        return (
                          <GridTickCell key={level} status="default">
                          </GridTickCell>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </SubjectsGrid>
            </>
          ) : (
            <DescriptionText className={'not-provided'}>
              No subjects provided.
            </DescriptionText>
          )}

          <SubheaderContainer>
            <SubheaderLine />
            <SubheaderTitle>Weekly Schedule</SubheaderTitle>
            <SubheaderLine />
          </SubheaderContainer>

          {tzSwitchButtons.length > 0 && (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {tzSwitchButtons.map(btn => (
                <button
                  key={btn.key}
                  onClick={() => setActiveTzKey(btn.key)}
                  style={{
                    all: 'unset', display: 'flex', alignItems: 'center', gap: '0.35rem',
                    padding: '0.3rem 0.65rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 500,
                    cursor: 'pointer', whiteSpace: 'nowrap', border: '1px solid var(--colors-sand7, #d4d4d4)',
                    color: 'var(--colors-sand12, #1a1a1a)', transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--colors-sand4, #f0f0f0)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <Globe size={13} />
                  View in {btn.label}
                </button>
              ))}
            </div>
          )}

          {availabilityStatusMap.size > 0 && (
            <ScheduleKeyContainer>
              <ScheduleKeyItem>
                <ScheduleKeyColorBox color="user1Only" />
                <ScheduleKeyLabel>Your Availability</ScheduleKeyLabel>
              </ScheduleKeyItem>
              <ScheduleKeyItem>
                <ScheduleKeyColorBox color="user2Only" />
                <ScheduleKeyLabel>Parent's Availability</ScheduleKeyLabel>
              </ScheduleKeyItem>
              <ScheduleKeyItem>
                <ScheduleKeyColorBox color="overlap" />
                <ScheduleKeyLabel>Overlap</ScheduleKeyLabel>
              </ScheduleKeyItem>
            </ScheduleKeyContainer>
          )}

          {availabilityStatusMap.size > 0 ? (
            <ScheduleGrid css={{ overflowX: 'auto' }}>
              <GridHeaderCell />
              {daysOfWeek.map((day) => (
                <GridHeaderCell key={day}>{day}</GridHeaderCell>
              ))}

              {hoursOfDay.map((minute) => (
                <React.Fragment key={minute}>
                  <TimeHeaderCell>
                    {new Date(0, 0, 0, 0, minute).toLocaleTimeString([], {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </TimeHeaderCell>

                  {daysOfWeek.map((day) => {
                    const key = `${day.toLowerCase()}-${minute}`;
                    const status = availabilityStatusMap.get(key) || 'empty';

                    return <ScheduleCell key={key} status={status as any} />;
                  })}
                </React.Fragment>
              ))}
            </ScheduleGrid>
          ) : (
            <DescriptionText className={'not-provided'}>
              No availability information found for this parent or yourself.
            </DescriptionText>
          )}
          {timezoneNote && availabilityStatusMap.size > 0 && (
            <p style={{ margin: '0.6rem 0 0', fontSize: '0.75rem', color: '#065f46', fontStyle: 'italic' }}>
              {timezoneNote}
            </p>
          )}
        </DetailContent>

        <DetailFooter>
          {showActions && (
            <>
              <SecondaryButton
                css={{ flex: '0 1 auto', minWidth: 0, marginRight: '0.5rem'}}
                onClick={() => {
                  handleSaveAndSearch();
                  onClose();
                }}
              >
                <ButtonText>
                Save & Continue Search
                </ButtonText>

                <Search size={16} />
              </SecondaryButton>

              <PrimaryButton
                onClick={() => {
                  handleSaveAndMessage();
                }}
                css={{ flex: '0 1 auto', minWidth: 0, marginLeft: '0.5rem'}}

              >
              <ButtonText css={{color: 'white'}}>
                Save & Message
                </ButtonText>

                <ChatLeftText color="white" size={16} />
              </PrimaryButton>
            </>
          )}
        </DetailFooter>
      {/* </DetailContentArea> */}
    </DetailPageShell3>
  );
};